// GlobalState.tsx
import React, {createContext, useState, useContext, ReactNode, useEffect} from 'react';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { fetchUserData } from "./util/FirestoreUserHelper";
import {resetMixpanel, setupMixpanel} from './util/TrackingHelper';
import { syncUserExperimentIdFromFirebase } from "./util/ExpirmentHelper";
import { trackEvent, TRACKING_EVENTS } from "./util/TrackingHelper";

interface GlobalFunctions {
    setUserProgressData: (userProgressData: any) => void;
    setShowPremiumOverlay: (showPremiumOverlay: boolean) => void;
    setShowAuthModal: (showAuthModal: boolean) => void;
    setAuthModalMode: (mode: 'login' | 'register') => void;
    setShowSuccessNotification:(showSuccessNotification: boolean) => void;
    setShowContentOverviewModal: (showContentOverviewModal: boolean) => void;
    setContentOverviewModalType: (type: string) => void;
    setShowEducatorInquiryModal: (showEducatorInquiryModal: boolean) => void;
}

interface GlobalState extends GlobalFunctions {
    userProgressData: any;
    user: any;
    isAuthenticated: boolean;
    isPremium: boolean;
    setUser: (user: any) => void;
    loadingUserProgress: boolean;
    showPremiumOverlay: boolean;
    showAuthModal: boolean;
    authModalMode: 'login' | 'register';
    isMobile: boolean;
    showSuccessNotification: boolean;
    showContentOverviewModal: boolean;
    contentOverviewModalType: string;
    showEducatorInquiryModal: boolean;
}

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

export const useGlobalState = (): GlobalState => {
    const context = useContext(GlobalStateContext);
    if (!context) {
        throw new Error("useGlobalState must be used within a GlobalStateProvider");
    }
    return context;
};

interface GlobalStateProviderProps {
    children: ReactNode;
}

export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({ children }) => {
    const [userProgressData, setUserProgressData] = useState<any>(null);
    const [user, setUser] = useState<any>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('register');
    const [loadingUserProgress, setLoadingUserProgress] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100);
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);
    const [showContentOverviewModal, setShowContentOverviewModal] = useState(false);
    const [contentOverviewModalType, setContentOverviewModalType] = useState('');
    const [showEducatorInquiryModal, setShowEducatorInquiryModal] = useState(false);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setIsAuthenticated(true);
                syncUserExperimentIdFromFirebase(currentUser.uid).then(() => {
                    setupMixpanel(currentUser)
                });
            } else {
                setIsAuthenticated(false);
                setIsPremium(false);
                setUser(null);
                resetMixpanel();
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1100);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    useEffect(() => {
        const loadUserProgress = async () => {
            setLoadingUserProgress(true);
            if (user) {
                const progressData = await fetchUserData(user.uid);
                if (progressData) {
                    setUserProgressData(progressData);
                    setIsPremium(progressData.isPremium || false);
                } else {
                    console.warn('❌ Failed to load user progress from Firestore');
                    setUserProgressData(null);
                    setIsPremium(false);
                }
            } else {
                setUserProgressData(null);
                setIsPremium(false);
            }
            setLoadingUserProgress(false);
        };

        loadUserProgress();
    }, [user]);

    // Handle payment success from Stripe redirect
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const planType = urlParams.get('plan');
        
        if (paymentSuccess === 'true' && user?.uid && !showSuccessNotification) {
            // Track successful trial start (value 2 - highest priority)
            trackEvent(TRACKING_EVENTS.PREMIUM_UPGRADE, { 
                user_id: user.uid, 
                plan_type: planType || ""
            });
            
            // Update user's premium status locally
            setUserProgressData((currentData: any) => ({
                ...currentData,
                isPremium: true,
                premiumUpgradeDate: new Date().toISOString(),
                planType: planType || 'premium'
            }));
            
            // Update premium state
            setIsPremium(true);
            
            // Show success notification
            setShowSuccessNotification(true);
            
            // Clean up URL parameters
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            
            console.log('✅ Premium conversion tracked for user:', user.uid);
            
            // Auto-hide notification after 5 seconds
            setTimeout(() => {
                setShowSuccessNotification(false);
            }, 5000);
        }
    }, [user, showSuccessNotification]);

    return (
        <GlobalStateContext.Provider value={{
            setUserProgressData,
            userProgressData,
            user,
            isAuthenticated,
            isPremium,
            setUser,
            loadingUserProgress,
            setShowPremiumOverlay,
            showPremiumOverlay,
            setShowAuthModal,
            showAuthModal,
            setAuthModalMode,
            authModalMode,
            isMobile,
            showSuccessNotification,
            setShowSuccessNotification,
            setShowContentOverviewModal,
            showContentOverviewModal,
            setContentOverviewModalType,
            contentOverviewModalType,
            setShowEducatorInquiryModal,
            showEducatorInquiryModal
        }}>
            {children}
        </GlobalStateContext.Provider>
    );
};